<template>
  <section
    class="home-hero-slider"
    @mouseenter="handleMouseEnter"
    @mousemove="handleMouseMove"
    @mouseleave="handleMouseLeave"
    @focusin="showArrowControls"
    @focusout="handleFocusOut"
  >
    <div class="home-hero-track-wrap">
      <div
        class="home-hero-track"
        :style="trackStyle"
        @touchstart.passive="handleTouchStart"
        @touchmove.passive="handleTouchMove"
        @touchend="handleTouchEnd"
      >
        <article
          v-for="(slide, index) in slides"
          :key="slide.id || index"
          class="home-hero-slide"
          :class="slide.themeClass"
          :aria-hidden="index !== currentIndex"
        >
          <div class="home-hero-copy">
            <h2 class="home-hero-title">{{ slide.title }}</h2>
            <p class="home-hero-description">{{ slide.description }}</p>

            <ul v-if="slide.points && slide.points.length" class="home-hero-points">
              <li v-for="point in slide.points" :key="point">{{ point }}</li>
            </ul>

            <div v-if="slide.actions && slide.actions.length" class="home-hero-actions">
              <button
                v-for="action in slide.actions"
                :key="`${slide.id}-${action.id || action.label}`"
                type="button"
                class="btn home-hero-action"
                :class="action.variant === 'secondary' ? 'btn-outline-light' : 'btn-light'"
                @click="emitAction(slide, action)"
              >
                {{ action.label }}
              </button>
            </div>
          </div>

          <div class="home-hero-media" :class="`media-count-${slide.media?.length || 0}`">
            <figure
              v-for="(item, mediaIndex) in slide.media"
              :key="`${slide.id}-media-${mediaIndex}`"
              class="home-hero-media-card"
              :class="{ primary: mediaIndex === 0 }"
            >
              <img :src="item.src" :alt="item.alt || slide.title" class="home-hero-media-image" />
              <figcaption v-if="item.caption" class="home-hero-media-caption">{{ item.caption }}</figcaption>
            </figure>
          </div>
        </article>
      </div>

      <button
        v-if="hasMultipleSlides"
        type="button"
        class="home-hero-arrow home-hero-arrow-prev"
        :class="{ 'home-hero-arrow-hidden': shouldHideArrows }"
        :aria-label="labels.previous"
        @click="goPrevious"
      >
        <i class="fas fa-chevron-left"></i>
      </button>

      <button
        v-if="hasMultipleSlides"
        type="button"
        class="home-hero-arrow home-hero-arrow-next"
        :class="{ 'home-hero-arrow-hidden': shouldHideArrows }"
        :aria-label="labels.next"
        @click="goNext"
      >
        <i class="fas fa-chevron-right"></i>
      </button>
    </div>

    <div v-if="hasMultipleSlides" class="home-hero-footer">
      <div class="home-hero-indicators" role="tablist" :aria-label="labels.pagination">
        <button
          v-for="(slide, index) in slides"
          :key="`${slide.id || index}-indicator`"
          type="button"
          class="home-hero-indicator"
          :class="{ active: index === currentIndex }"
          :aria-label="`${labels.goTo} ${slide.title}`"
          :aria-pressed="index === currentIndex"
          @click="goTo(index)"
        ></button>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: 'HomeHeroSlider',
  ARROW_HIDE_DELAY_MS: 1400,
  props: {
    slides: {
      type: Array,
      default: () => [],
    },
    labels: {
      type: Object,
      default: () => ({
        previous: 'Previous slide',
        next: 'Next slide',
        pagination: 'Slide navigation',
        goTo: 'Go to',
      }),
    },
    autoplayMs: {
      type: Number,
      default: 6500,
    },
  },
  data() {
    return {
      currentIndex: 0,
      autoplayTimer: null,
      isPaused: false,
      touchStartX: 0,
      touchCurrentX: 0,
      showNavigationArrows: true,
      arrowHideTimer: null,
      autoHideDesktopArrows: false,
    };
  },
  computed: {
    hasMultipleSlides() {
      return this.slides.length > 1;
    },
    activeSlide() {
      return this.slides[this.currentIndex] || {};
    },
    trackStyle() {
      return {
        transform: `translateX(-${this.currentIndex * 100}%)`,
      };
    },
    shouldHideArrows() {
      return this.autoHideDesktopArrows && !this.showNavigationArrows;
    },
  },
  watch: {
    slides: {
      immediate: true,
      handler(nextSlides) {
        if (!nextSlides.length) {
          this.currentIndex = 0;
          this.stopAutoplay();
          return;
        }

        if (this.currentIndex > nextSlides.length - 1) {
          this.currentIndex = 0;
        }

        this.restartAutoplay();
      },
    },
  },
  mounted() {
    this.autoHideDesktopArrows = this.detectDesktopPointer();
    this.showNavigationArrows = !this.autoHideDesktopArrows;
    this.restartAutoplay();
  },
  beforeUnmount() {
    this.clearArrowHideTimer();
    this.stopAutoplay();
  },
  methods: {
    detectDesktopPointer() {
      if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
        return false;
      }

      return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    },
    clearArrowHideTimer() {
      if (this.arrowHideTimer) {
        window.clearTimeout(this.arrowHideTimer);
        this.arrowHideTimer = null;
      }
    },
    scheduleArrowHide() {
      if (!this.autoHideDesktopArrows) {
        this.showNavigationArrows = true;
        return;
      }

      this.clearArrowHideTimer();
      this.arrowHideTimer = window.setTimeout(() => {
        this.showNavigationArrows = false;
        this.arrowHideTimer = null;
      }, this.$options.ARROW_HIDE_DELAY_MS);
    },
    showArrowControls() {
      this.showNavigationArrows = true;
      this.scheduleArrowHide();
    },
    handleMouseEnter() {
      this.pauseAutoplay();
    },
    handleMouseMove() {
      this.showArrowControls();
    },
    handleMouseLeave() {
      this.clearArrowHideTimer();
      if (this.autoHideDesktopArrows) {
        this.showNavigationArrows = false;
      }
      this.resumeAutoplay();
    },
    handleFocusOut() {
      this.scheduleArrowHide();
    },
    emitAction(slide, action) {
      this.$emit('action', { slide, action, index: this.currentIndex });
    },
    goTo(index) {
      if (!this.slides.length) return;
      this.currentIndex = index;
      this.restartAutoplay();
    },
    goPrevious() {
      if (!this.hasMultipleSlides) return;
      this.currentIndex = (this.currentIndex - 1 + this.slides.length) % this.slides.length;
      this.restartAutoplay();
    },
    goNext() {
      if (!this.hasMultipleSlides) return;
      this.currentIndex = (this.currentIndex + 1) % this.slides.length;
      this.restartAutoplay();
    },
    restartAutoplay() {
      this.stopAutoplay();

      if (!this.hasMultipleSlides || this.isPaused) {
        return;
      }

      this.autoplayTimer = window.setInterval(() => {
        this.goNext();
      }, this.autoplayMs);
    },
    stopAutoplay() {
      if (this.autoplayTimer) {
        window.clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    },
    pauseAutoplay() {
      this.isPaused = true;
      this.stopAutoplay();
    },
    resumeAutoplay() {
      this.isPaused = false;
      this.restartAutoplay();
    },
    handleTouchStart(event) {
      const touch = event.changedTouches?.[0];
      this.touchStartX = touch?.clientX || 0;
      this.touchCurrentX = this.touchStartX;
      this.showNavigationArrows = true;
      this.pauseAutoplay();
    },
    handleTouchMove(event) {
      const touch = event.changedTouches?.[0];
      this.touchCurrentX = touch?.clientX || this.touchStartX;
    },
    handleTouchEnd() {
      const deltaX = this.touchCurrentX - this.touchStartX;

      if (Math.abs(deltaX) > 40) {
        if (deltaX > 0) {
          this.goPrevious();
        } else {
          this.goNext();
        }
      }

      this.resumeAutoplay();
    },
  },
};
</script>

<style scoped>
.home-hero-slider {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.home-hero-track-wrap {
  position: relative;
  overflow: hidden;
}

.home-hero-track {
  display: flex;
  align-items: flex-start;
  transition: transform 0.45s ease;
  will-change: transform;
}

.home-hero-slide {
  min-width: 100%;
  display: grid;
  grid-template-columns: minmax(0, 1.05fr) minmax(0, 1fr);
  gap: 28px;
  padding: 32px;
  color: #0f172a;
  background: transparent;
  border: 0;
  box-shadow: none;
  min-height: 520px;
}

.home-hero-slide.theme-profile {
  background: transparent;
}

.home-hero-slide.theme-tricks {
  background: transparent;
}

.home-hero-slide.theme-rulebooks {
  background: transparent;
}

.home-hero-slide.theme-training {
  background: transparent;
}

.home-hero-slide.theme-competitions {
  background: transparent;
}

.home-hero-slide.theme-community {
  background: transparent;
}

.home-hero-copy {
  display: flex;
  flex-direction: column;
  gap: 18px;
  justify-content: center;
  align-items: center;
  text-align: center;
}

.home-hero-title {
  margin: 0;
  font-size: clamp(2rem, 3vw, 3.35rem);
  line-height: 1.02;
  max-width: 11ch;
  align-self: center;
  text-align: center;
}

.home-hero-description {
  margin: 0;
  font-size: 1.05rem;
  line-height: 1.65;
  max-width: 60ch;
  color: #475569;
}

.home-hero-points {
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  gap: 10px;
}

.home-hero-points li {
  position: relative;
  padding-left: 20px;
  color: #334155;
}

.home-hero-points li::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0.6em;
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: #5eead4;
}

.home-hero-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 4px;
  justify-content: center;
}

.home-hero-action {
  min-height: 46px;
  border-radius: 999px;
  padding: 0 18px;
  font-weight: 600;
}

.home-hero-action.btn-light {
  background: #0f172a;
  border-color: #0f172a;
  color: #f8fafc;
}

.home-hero-action.btn-light:hover,
.home-hero-action.btn-light:focus {
  background: #1e293b;
  border-color: #1e293b;
  color: #f8fafc;
}

.home-hero-action.btn-outline-light {
  background: rgba(255, 255, 255, 0.86);
  border-color: rgba(148, 163, 184, 0.34);
  color: #0f172a;
}

.home-hero-action.btn-outline-light:hover,
.home-hero-action.btn-outline-light:focus {
  background: rgba(241, 245, 249, 0.98);
  border-color: rgba(148, 163, 184, 0.46);
  color: #0f172a;
}

.home-hero-media {
  display: grid;
  grid-template-columns: minmax(0, 1.15fr) minmax(0, 0.85fr);
  gap: 16px;
  align-items: stretch;
}

.home-hero-media.media-count-1 {
  grid-template-columns: minmax(0, 1fr);
}

.home-hero-media.media-count-2 {
  position: relative;
  display: block;
  min-height: 430px;
}

.home-hero-media.media-count-3 {
  position: relative;
  display: block;
  min-height: 470px;
}

.home-hero-media-card {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px;
  margin: 0;
  overflow: hidden;
  border-radius: 22px;
  background: rgba(255, 255, 255, 0.84);
  border: 1px solid rgba(148, 163, 184, 0.22);
  box-shadow: 0 18px 40px rgba(15, 23, 42, 0.1);
  backdrop-filter: blur(10px);
}

.home-hero-media.media-count-2 .home-hero-media-card {
  position: absolute;
  width: 76%;
}

.home-hero-media.media-count-3 .home-hero-media-card {
  position: absolute;
}

.home-hero-media-card.primary {
  transform: translateY(14px);
}

.home-hero-media.media-count-2 .home-hero-media-card.primary {
  top: 0;
  left: 0;
  z-index: 2;
  transform: none;
}

.home-hero-media.media-count-2 .home-hero-media-card:not(.primary) {
  right: 0;
  bottom: 0;
  width: 64%;
  z-index: 1;
}

.home-hero-media.media-count-3 .home-hero-media-card.primary {
  top: 0;
  left: 0;
  width: 78%;
  z-index: 3;
  transform: none;
}

.home-hero-media.media-count-3 .home-hero-media-card:nth-child(2) {
  right: 0;
  top: 214px;
  width: 54%;
  z-index: 2;
}

.home-hero-media.media-count-3 .home-hero-media-card:nth-child(3) {
  left: 18%;
  bottom: 0;
  width: 58%;
  z-index: 1;
}

.home-hero-media-image {
  display: block;
  width: 100%;
  aspect-ratio: 16 / 10;
  height: auto;
  min-height: 180px;
  object-fit: cover;
  border-radius: 16px;
  background: rgba(148, 163, 184, 0.08);
}

.home-hero-media-caption {
  position: absolute;
  left: 22px;
  right: 22px;
  bottom: 18px;
  z-index: 2;
  margin: 0;
  padding: 10px 12px;
  border-radius: 14px;
  font-size: 0.86rem;
  color: rgba(248, 250, 252, 0.92);
  background: linear-gradient(180deg, rgba(15, 23, 42, 0.18) 0%, rgba(15, 23, 42, 0.68) 100%);
  border: 1px solid rgba(148, 163, 184, 0.14);
  backdrop-filter: blur(8px);
}

.home-hero-arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 48px;
  height: 48px;
  border: 0;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(148, 163, 184, 0.24);
  color: #0f172a;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 12px 24px rgba(15, 23, 42, 0.12);
  z-index: 2;
  opacity: 1;
  transition: opacity 0.22s ease, transform 0.22s ease;
}

.home-hero-arrow-hidden {
  opacity: 0;
  pointer-events: none;
}

.home-hero-arrow-prev {
  left: 16px;
}

.home-hero-arrow-next {
  right: 16px;
}

.home-hero-footer {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  flex-wrap: wrap;
}

.home-hero-indicators {
  display: inline-flex;
  align-items: center;
  gap: 10px;
}

.home-hero-indicator {
  width: 12px;
  height: 12px;
  border-radius: 999px;
  border: 0;
  background: rgba(148, 163, 184, 0.52);
}

.home-hero-indicator.active {
  width: 34px;
  background: #0f172a;
}

@media (max-width: 991.98px) {
  .home-hero-slide {
    grid-template-columns: minmax(0, 1fr);
    grid-auto-rows: max-content;
    min-height: auto;
    align-items: start;
    align-content: start;
    gap: 18px;
  }

  .home-hero-title {
    max-width: none;
  }

  .home-hero-copy {
    justify-content: flex-start;
    align-self: start;
    height: max-content;
  }

  .home-hero-media {
    align-self: start;
    align-items: start;
    align-content: start;
    height: max-content;
  }

  .home-hero-media-card {
    align-self: start;
  }

  .home-hero-media.media-count-2 {
    min-height: 380px;
  }

  .home-hero-media.media-count-3 {
    min-height: 420px;
  }

  .home-hero-media.media-count-2 .home-hero-media-card:not(.primary) {
    width: 68%;
  }

  .home-hero-media.media-count-3 .home-hero-media-card.primary {
    width: 74%;
  }

  .home-hero-media.media-count-3 .home-hero-media-card:nth-child(2) {
    top: 188px;
    width: 58%;
  }

  .home-hero-media.media-count-3 .home-hero-media-card:nth-child(3) {
    left: 10%;
    width: 62%;
  }

  .home-hero-media-card.primary {
    transform: none;
  }
}

@media (max-width: 767.98px) {
  .home-hero-track {
    align-items: flex-start;
  }

  .home-hero-slide {
    padding: 22px;
    grid-auto-rows: max-content;
    align-content: start;
    gap: 12px;
  }

  .home-hero-copy {
    justify-content: flex-start;
    align-items: center;
    text-align: center;
    align-self: start;
    height: max-content;
    gap: 12px;
  }

  .home-hero-media {
    grid-template-columns: minmax(0, 1fr);
    align-self: start;
    align-items: start;
    align-content: start;
    height: max-content;
  }

  .home-hero-media.media-count-2 {
    display: grid;
    min-height: auto;
    gap: 14px;
  }

  .home-hero-media.media-count-3 {
    display: grid;
    min-height: auto;
    gap: 14px;
  }

  .home-hero-media.media-count-2 .home-hero-media-card,
  .home-hero-media.media-count-2 .home-hero-media-card.primary,
  .home-hero-media.media-count-2 .home-hero-media-card:not(.primary),
  .home-hero-media.media-count-3 .home-hero-media-card,
  .home-hero-media.media-count-3 .home-hero-media-card.primary,
  .home-hero-media.media-count-3 .home-hero-media-card:nth-child(2),
  .home-hero-media.media-count-3 .home-hero-media-card:nth-child(3) {
    position: relative;
    inset: auto;
    width: 100%;
    transform: none;
  }

  .home-hero-arrow {
    width: 42px;
    height: 42px;
    top: auto;
    bottom: 16px;
    transform: none;
  }

  .home-hero-arrow-prev {
    left: auto;
    right: 68px;
  }

  .home-hero-arrow-next {
    right: 16px;
  }

  .home-hero-action {
    width: 100%;
    justify-content: center;
  }

  .home-hero-footer {
    align-items: flex-start;
    flex-direction: column;
  }
}
</style>